import { useCallback, useMemo, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../server/routers";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import { useApplyDefaultLocation } from "@/hooks/useDefaultLocation";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, FileBarChart, Loader2, Printer, RefreshCw } from "lucide-react";

/**
 * Admin-only Reports page. Pick a report + its options, Generate, then
 * "Print / Save as PDF" (browser print dialog). The report renders as a
 * white "sheet" so what's on screen matches the printout.
 *
 * Reports so far:
 *  - Provider Clips Quarterly Report (server: src/server/reports.ts)
 */

const LOCATIONS = ["Iowa", "Minnesota", "Colorado"] as const;
type Location = (typeof LOCATIONS)[number];

// Same palette as the Location chips elsewhere (Recents / Footage Search):
// Iowa blue, Minnesota purple, Colorado green.
const REGION_COLORS: Record<Location, string> = {
  Iowa: "#5B7C99",
  Minnesota: "#6A5ACD",
  Colorado: "#1b6e4a",
};

const REPORTS = [{ id: "provider-clips-quarterly", label: "Provider Clips Quarterly Report" }] as const;

/** First quarter the dropdown offers. */
const FIRST_YEAR = 2026;

interface QuarterOption {
  value: string; // "2026-3"
  label: string; // "2026 Q3"
  year: number;
  quarter: number;
}

/** "2026 Q1" up to the current quarter, newest first. */
function quarterOptions(now = new Date()): QuarterOption[] {
  const out: QuarterOption[] = [];
  const curYear = now.getFullYear();
  const curQ = Math.floor(now.getMonth() / 3) + 1;
  for (let y = FIRST_YEAR; y <= curYear; y++) {
    for (let q = 1; q <= 4; q++) {
      if (y === curYear && q > curQ) break;
      out.push({ value: `${y}-${q}`, label: `${y} Q${q}`, year: y, quarter: q });
    }
  }
  return out.reverse();
}

/** Default to the most recently finished quarter (that's what a quarterly
 *  report is usually for); fall back to the current one. */
function defaultQuarter(opts: QuarterOption[]): string {
  return (opts[1] ?? opts[0]).value;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtDay(iso: string, withYear = false): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}${withYear ? `, ${y}` : ""}`;
}

function fmtDuration(totalSeconds: number): string {
  const mins = Math.round(totalSeconds / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function initials(name: string): string {
  const parts = name.replace(/^dr\.?\s+/i, "").split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
}

export default function Reports() {
  const { loading } = useAuth({ redirectOnUnauthenticated: true });
  const [, navigate] = useLocation();

  const quarters = useMemo(() => quarterOptions(), []);
  const [reportId, setReportId] = useState<string>(REPORTS[0].id);
  const [location, setLocation] = useState<Location | null>(null);
  const setLocationCb = useCallback((v: Location) => setLocation(v), []);
  useApplyDefaultLocation(location, setLocationCb);
  const [quarterValue, setQuarterValue] = useState(() => defaultQuarter(quarters));

  // The report only runs when Generate is clicked (the first run of a
  // quarter can take ~30s while clip lengths are fetched from Drive).
  const [params, setParams] = useState<{ location: Location; year: number; quarter: number } | null>(null);
  const reportQuery = trpc.admin.providerClipsQuarterlyReport.useQuery(params!, {
    enabled: !!params,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const report = reportQuery.data;
  const busy = reportQuery.isFetching;

  const selected = quarters.find((q) => q.value === quarterValue);
  const canGenerate = !!location && !!selected && !busy;
  const sameAsShown =
    params && selected && params.location === location &&
    params.year === selected.year && params.quarter === selected.quarter;

  const generate = () => {
    if (!location || !selected) return;
    const next = { location, year: selected.year, quarter: selected.quarter };
    if (sameAsShown) void reportQuery.refetch();
    else setParams(next);
  };

  const print = () => {
    if (!report) return;
    // The document title becomes the default PDF filename.
    const prev = document.title;
    document.title = `Provider Clips Quarterly Report - ${report.location} ${report.year} Q${report.quarter}`;
    const restore = () => {
      document.title = prev;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      {/* Landscape so the week-by-week table fits across the page. Only
          mounted on this page, so other pages' printing is unaffected. */}
      <style>{`@media print { @page { size: letter landscape; margin: 0.4in; } }`}</style>

      <main className="max-w-6xl mx-auto px-4 py-8 print:p-0 print:max-w-none">
        <div className="flex items-center gap-3 mb-6 print-hidden">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-1">
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          <FileBarChart className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Reports</h1>
            <p className="text-sm text-muted-foreground">
              Generate a report, then print it or save it as a PDF. Admin access only.
            </p>
          </div>
        </div>

        <Card className="mb-6 print-hidden">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-end gap-4">
              <Field label="Report">
                <Select value={reportId} onValueChange={setReportId}>
                  <SelectTrigger className="w-[280px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORTS.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Location">
                <Select value={location ?? ""} onValueChange={(v) => setLocation(v as Location)}>
                  <SelectTrigger className="w-[170px]">
                    <SelectValue placeholder="Choose location" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map((l) => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Quarter">
                <Select value={quarterValue} onValueChange={setQuarterValue}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {quarters.map((q) => (
                      <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Button onClick={generate} disabled={!canGenerate} className="gap-1.5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBarChart className="w-4 h-4" />}
                {busy ? "Generating…" : sameAsShown ? "Regenerate" : "Generate"}
              </Button>
              <Button variant="outline" onClick={print} disabled={!report || busy} className="gap-1.5">
                <Printer className="w-4 h-4" />
                Print / Save as PDF
              </Button>
            </div>
            {busy && (
              <p className="mt-3 text-xs text-muted-foreground">
                The first run for a quarter can take up to half a minute while clip lengths are looked up in Google Drive.
              </p>
            )}
            {reportQuery.error && (
              <p className="mt-3 text-sm text-red-600">Couldn't generate the report: {reportQuery.error.message}</p>
            )}
          </CardContent>
        </Card>

        {report && !busy && <ProviderClipsReport report={report} />}
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

type ReportData = inferRouterOutputs<AppRouter>["admin"]["providerClipsQuarterlyReport"];

function ProviderClipsReport({ report }: { report: ReportData }) {
  const color = REGION_COLORS[report.location];
  const { totals, weeks, providers } = report;
  const maxCell = Math.max(1, ...providers.flatMap((p) => p.weekly));
  const generated = new Date(report.generatedAt).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <div
      className="bg-white text-slate-900 rounded-lg border border-slate-200 shadow-sm p-8 print:border-0 print:shadow-none print:p-0 print:rounded-none"
      style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
    >
      {/* Title block */}
      <div className="flex items-start justify-between gap-6 border-b-4 pb-4 mb-6" style={{ borderColor: color }}>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider" style={{ color }}>
            Infinity Neuromotor · {report.location}
          </div>
          <h2 className="text-2xl font-bold mt-1">Provider Clips Quarterly Report</h2>
          <div className="text-sm text-slate-600 mt-1">
            {report.year} Q{report.quarter} · {fmtDay(report.periodStart)} – {fmtDay(report.periodEnd, true)}
            {report.inProgress && <span className="ml-2 text-amber-700 font-medium">(quarter in progress — partial)</span>}
          </div>
        </div>
        <div className="text-right text-xs text-slate-500 shrink-0">Generated {generated}</div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-3 gap-4 mb-2">
        <Stat label="Clips filmed" value={totals.clips.toLocaleString()} color={color} />
        <Stat label="Total footage time" value={fmtDuration(totals.durationSeconds)} color={color} />
        <Stat label="Patients filmed" value={totals.patients.toLocaleString()} color={color} />
      </div>
      <div className="text-[11px] text-slate-500 mb-6 space-y-0.5">
        {totals.durationUnknown > 0 && (
          <div>
            Footage time leaves out {totals.durationUnknown.toLocaleString()} clip
            {totals.durationUnknown === 1 ? "" : "s"} whose length isn't known (e.g. audio files).
          </div>
        )}
        {totals.durationPending > 0 && (
          <div className="text-amber-700 print-hidden">
            {totals.durationPending.toLocaleString()} clip length{totals.durationPending === 1 ? " is" : "s are"} still
            being looked up — click Regenerate to finish before printing.
          </div>
        )}
      </div>

      {/* Per-provider, week by week */}
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">
        Clips by Associated Provider, per week
      </h3>
      {providers.length === 0 ? (
        <p className="text-sm text-slate-500">No providers in the {report.location} Provider library yet.</p>
      ) : (
        <table className="w-full border-collapse text-xs tabular-nums">
          <thead>
            <tr className="border-b border-slate-300 text-slate-500">
              <th className="text-left font-medium py-1.5 pr-2">Provider</th>
              {weeks.map((w) => (
                <th
                  key={w.weekStart}
                  className="font-medium py-1.5 px-0.5 text-center leading-tight"
                  title={`${fmtDay(w.rangeStart)} – ${fmtDay(w.rangeEnd)}`}
                >
                  {fmtDay(w.rangeStart).replace(" ", " ")}
                </th>
              ))}
              <th className="font-semibold py-1.5 pl-2 text-right text-slate-700">Total</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <tr key={`${p.kind}:${p.name}`} className="border-b border-slate-100 break-inside-avoid">
                <td className="py-1.5 pr-2">
                  <div className="flex items-center gap-2 min-w-[150px]">
                    <Headshot name={p.name} photoUrl={p.photoUrl} kind={p.kind} color={color} />
                    <div className="leading-tight">
                      <div className={p.kind === "provider" ? "font-medium" : "italic text-slate-600"}>{p.name}</div>
                      {p.kind === "unlisted" && <div className="text-[10px] text-slate-400">not in {report.location} library</div>}
                      {p.status === "hiatus" && <div className="text-[10px] text-slate-400">on hiatus</div>}
                      {p.status === "former" && <div className="text-[10px] text-slate-400">no longer employed</div>}
                    </div>
                  </div>
                </td>
                {p.weekly.map((n, i) => (
                  <td key={i} className="p-0.5 text-center">
                    <div
                      className="rounded py-1"
                      style={
                        n > 0
                          ? { backgroundColor: hexA(color, 0.12 + 0.5 * (n / maxCell)), color: n / maxCell > 0.6 ? "white" : undefined }
                          : undefined
                      }
                    >
                      {n > 0 ? n : <span className="text-slate-300">–</span>}
                    </div>
                  </td>
                ))}
                <td className="py-1.5 pl-2 text-right font-semibold text-sm">{p.total}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 font-semibold">
              <td className="py-1.5 pr-2">All clips</td>
              {totals.weekly.map((n, i) => (
                <td key={i} className="py-1.5 text-center">{n || <span className="text-slate-300">–</span>}</td>
              ))}
              <td className="py-1.5 pl-2 text-right text-sm">{totals.clips}</td>
            </tr>
          </tfoot>
        </table>
      )}

      <div className="mt-4 text-[10px] text-slate-500 leading-relaxed">
        Patient footage filmed {fmtDay(report.periodStart)} – {fmtDay(report.periodEnd, true)} for {report.location}, by the
        date it was filmed (its dated session folder); clips with no known filming date use their upload date. Weeks run Monday–Sunday; each column is headed by its first day in the quarter. A clip tagged
        with more than one provider counts once for each, so provider rows can add up to more than "All clips".
        {" "}{totals.clipsWithProvider.toLocaleString()} of {totals.clips.toLocaleString()} clips have an Associated Provider.
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="rounded-lg border border-slate-200 px-4 py-3" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-3xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function Headshot({
  name,
  photoUrl,
  kind,
  color,
}: {
  name: string;
  photoUrl: string | null;
  kind: "provider" | "unlisted" | "none";
  color: string;
}) {
  if (photoUrl) {
    return <img src={photoUrl} alt="" className="w-8 h-8 rounded-full object-cover shrink-0 border border-slate-200" />;
  }
  return (
    <div
      className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[11px] font-semibold"
      style={kind === "provider" ? { backgroundColor: hexA(color, 0.15), color } : { backgroundColor: "#f1f5f9", color: "#94a3b8" }}
    >
      {kind === "none" ? "–" : initials(name)}
    </div>
  );
}

/** "#5B7C99" + alpha → "rgba(91,124,153,a)". */
function hexA(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha.toFixed(2)})`;
}
